/**
 * Mission Control client for the AHP relay path.
 *
 * Mints the Web PubSub credentials a browser client needs to open a relay
 * connection to an environment's agent daemon. The connect endpoint returns a
 * short-lived token plus the per-client group lanes (`broadcast`, `to_client`,
 * `to_host`) the WPS transport joins and publishes to.
 *
 * The network call is supplied by the caller as an {@link MCFetcher} so this
 * package stays free of any host application's auth / base-URL handling.
 */

/** API path that mints WPS credentials for an environment, scoped to a session. */
export function environmentConnectApiPath(environmentId: string, sessionId: string): string {
  const params = new URLSearchParams({session_id: sessionId})
  return `/agents/environments/${encodeURIComponent(environmentId)}/connect?${params.toString()}`
}

/** API path that refreshes WPS credentials for an existing relay client connection. */
export function environmentReconnectApiPath(environmentId: string, clientId: string): string {
  const params = new URLSearchParams({client_id: clientId})
  return `/agents/environments/${encodeURIComponent(environmentId)}/reconnect?${params.toString()}`
}

/**
 * API path that mints WPS credentials AND asks Mission Control to pre-create
 * a session bound to the environment. The response carries an optional
 * `session_id` (and, once exposed by MC, `task_id`).
 */
export function environmentConnectCreateSessionApiPath(environmentId: string): string {
  return `/agents/environments/${encodeURIComponent(environmentId)}/connect?create_session=true`
}

/** Per-client group lanes the WPS transport joins / publishes to. */
export interface MCConnectGroups {
  broadcast: string
  to_client: string
  to_host: string
}

// A public sealing key the host advertises and MC reseals the user's GitHub
// token to. The `key_id` is also embedded in the sealed envelope.
export interface AdvertisedEncryptionKey {
  key_id: string
  use: string
  algorithm: string
  public_key: string
}

/** Response from the connect/reconnect endpoints — everything needed to open WPS. */
export interface MCConnectResponse {
  access_token: string
  expires_at: string
  wps_endpoint: string
  hub: string
  subprotocol: string
  client_id: string
  groups: MCConnectGroups
  // The user's GitHub token resealed to the host's auth-token key, forwarded
  // verbatim as the AHP `authenticate` token. Absent if the host advertised no key.
  encrypted_github_token?: string
  host_encryption_key?: AdvertisedEncryptionKey
}

/**
 * Response from `/connect?create_session=true`. Same WPS credential fields as
 * {@link MCConnectResponse}, plus optional identifiers for the session (and
 * task, once MC adds it) that Mission Control pre-created on the caller's
 * behalf. Either may be absent if MC accepted the connect but couldn't
 * complete the pre-create (best-effort contract); the connection itself is
 * still usable.
 */
export interface MCConnectWithSessionResponse extends MCConnectResponse {
  session_id?: string
  task_id?: string
}

export class MissionControlError extends Error {
  readonly status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.status = status
    this.name = 'MissionControlError'
  }
}

/** Abstraction over the network call, supplied by the consuming application. */
export type MCFetcher = (path: string) => Promise<Response>

/**
 * Validate the fields the WPS transport depends on before building a connection
 * out of them, so a malformed response fails here with a clear error rather
 * than later as an opaque transport failure.
 */
export function assertConnectShape(resp: MCConnectResponse, what: string): MCConnectResponse {
  if (!resp || typeof resp !== 'object') {
    throw new MissionControlError(`${what} returned a non-object response`)
  }
  const missing: string[] = []
  if (!resp.access_token) missing.push('access_token')
  if (!resp.wps_endpoint) missing.push('wps_endpoint')
  if (!resp.client_id) missing.push('client_id')
  if (!resp.groups || typeof resp.groups !== 'object') {
    missing.push('groups')
  } else {
    if (!resp.groups.broadcast) missing.push('groups.broadcast')
    if (!resp.groups.to_client) missing.push('groups.to_client')
    if (!resp.groups.to_host) missing.push('groups.to_host')
  }
  if (missing.length > 0) {
    throw new MissionControlError(`${what} response missing required field(s): ${missing.join(', ')}`)
  }
  return resp
}

/**
 * Request WPS credentials for an environment, scoped to a single session.
 * Mission Control mints a fresh token + client_id and notifies the
 * environment's daemon that a client has connected.
 */
export async function connectToEnvironment(
  environmentId: string,
  sessionId: string,
  fetcher: MCFetcher,
): Promise<MCConnectResponse> {
  const response = await fetcher(environmentConnectApiPath(environmentId, sessionId))
  const body = (await response.json()) as MCConnectResponse
  return assertConnectShape(body, 'connect')
}

/** Refresh the WPS token for an existing relay client connection. */
export async function reconnectToEnvironment(
  environmentId: string,
  clientId: string,
  fetcher: MCFetcher,
): Promise<MCConnectResponse> {
  const response = await fetcher(environmentReconnectApiPath(environmentId, clientId))
  const body = (await response.json()) as MCConnectResponse
  return assertConnectShape(body, 'reconnect')
}

/**
 * Mint WPS credentials AND ask Mission Control to pre-create a session bound
 * to the environment. The returned `session_id`/`task_id` are best-effort:
 * present when MC successfully created the placeholder session before
 * returning, absent if the pre-create failed (the connection itself is still
 * valid).
 */
export async function connectAndCreateSession(
  environmentId: string,
  fetcher: MCFetcher,
): Promise<MCConnectWithSessionResponse> {
  const response = await fetcher(environmentConnectCreateSessionApiPath(environmentId))
  const body = (await response.json()) as MCConnectWithSessionResponse
  assertConnectShape(body, 'connect')
  return body
}

/**
 * Build the WPS WebSocket URL from a connect/reconnect response: the endpoint
 * with the access token attached as the `access_token` query parameter, and a
 * `ws(s)://` scheme guaranteed.
 */
export function buildWpsUrl(resp: Pick<MCConnectResponse, 'wps_endpoint' | 'access_token'>): string {
  let raw = resp.wps_endpoint
  if (!raw.startsWith('wss://') && !raw.startsWith('ws://')) {
    raw = `wss://${raw}`
  }
  // wps_endpoint is always an absolute ws(s):// URL, so no base is required.
  // eslint-disable-next-line no-restricted-syntax
  const url = new URL(raw)
  url.searchParams.set('access_token', resp.access_token)
  return url.toString()
}
